import React from 'react';
import {
    CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography
} from '@material-ui/core/';
import PubSub from 'pubsub-js';
import Moment from 'moment';

import Axios from '@apricityhealth/web-common-lib/utils/Axios';
import Config from '@apricityhealth/web-common-lib/Config';
import getErrorMessage from '@apricityhealth/web-common-lib/utils/getErrorMessage';

import { loadJobResult } from '../../utils/LoadJobResults';
import CurveChart from './CurveChart';
import HeatmapChart from './HeatmapChart';
import ResultTables from './ResultTables';
import MetricsPanel from './MetricsPanel';

// Python writes NaN and Infinity, which JSON.parse rejects outright — see json_parseMore.
require('../../utils/json_parseMore');

const JOB_POLL_INTERVAL = 1000 * 5;

/**
 * One renderer per result format.
 *
 * Each reads ONLY its own key out of the result and ignores everything else, which is what lets an algorithm
 * keep whatever bespoke output it likes and still be drawable: `supervised-risk` returns folds, labelling and
 * warnings that no view here knows about, and its `metrics` map renders anyway.
 */
const RENDERERS = {
    curve: { title: 'Curves', empty: 'no `curves` block', render: (data) => <CurveChart data={data} /> },
    heatmap: { title: 'Heatmaps', empty: 'no `heatmaps` block', render: (data) => <HeatmapChart data={data} /> },
    table: { title: 'Tables', empty: 'no `tables` block', render: (data) => <ResultTables data={data} /> },
    metrics: { title: 'Metrics', empty: 'no `metrics` block', render: (data) => <MetricsPanel data={data} /> }
};

/**
 * The generic job-result viewer — pick a job, draw its result.
 *
 * **Selects jobs by result FORMAT, never by algorithm name.** 🔒 *Richard, 2026-08-27: the old views filtered
 * on `"kmeans,kmeans-generative,knn"`, so an algorithm added through Sage could not be visualised at all.* A
 * job's `resultFormat` comes from its algorithm's manifest, which means a researcher's own algorithm appears
 * here as soon as it declares one.
 *
 * The load/poll/fetch cycle is the same one `pcaView` and `clusteringView` each carry their own copy of. It
 * lives here once because four more copies of it was the alternative. Those two keep theirs: their Three.js
 * lifecycle is tangled into it, and rewriting a working 3D scene to save duplication is not a trade worth
 * making today.
 */
export default class JobResultView extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            jobs: [],
            selectedJobId: '',
            job: null,
            data: null,
            progress: null,
            loading: null,
            error: null
        };
    }

    componentDidMount() {
        this.loadContent();
        this.token = PubSub.subscribe('PLAN_TOPIC', this.loadContent.bind(this));
    }

    componentWillUnmount() {
        this.clearTimer();
        PubSub.unsubscribe(this.token);
    }

    /**
     * Switching format without remounting has to drop the loaded result.
     *
     * Both routes render this component, so React reuses the instance when the user moves from Curves to
     * Tables — leaving the previous job's data on screen under the new heading, filtered by the old format.
     */
    componentDidUpdate(prevProps) {
        if (prevProps.resultFormat !== this.props.resultFormat) {
            this.clearTimer();
            this.loadingJob = null;
            this.setState({
                jobs: [], selectedJobId: '', job: null, data: null, loading: null, error: null
            }, this.loadContent.bind(this));
        }
    }

    clearTimer() {
        if (this.loadingTimer) {
            clearTimeout(this.loadingTimer);
            this.loadingTimer = null;
        }
    }

    get resultFormat() {
        return this.props.resultFormat;
    }

    loadContent() {
        const { appContext: { state: { plan, idToken } } } = this.props;
        const planId = plan && plan.planId;
        if (!planId || !idToken) return;

        this.setState({ progress: <CircularProgress size={20} />, error: null });
        const getMLJobs = {
            url: Config.baseUrl + `${Config.pathPrefix}apricity-forecast/${planId}/ml/job/`
                + `*?resultFormat=${encodeURIComponent(this.resultFormat)}&status=done,created,running`,
            method: 'GET',
            headers: { Authorization: idToken }
        };
        Axios(getMLJobs).then(({ data: { jobs } }) => {
            this.setState({ jobs: jobs || [], progress: null });
        }).catch((error) => {
            this.setState({ progress: null, error: getErrorMessage(error) });
        });
    }

    loadJob() {
        const { selectedJobId } = this.state;
        const { appContext: { state: { plan, idToken } } } = this.props;
        const planId = plan && plan.planId;
        if (!selectedJobId || this.loadingJob === selectedJobId || !planId || !idToken) return;

        this.loadingJob = selectedJobId;
        const getMLJob = {
            url: Config.baseUrl + `${Config.pathPrefix}apricity-forecast/${planId}/ml/job/${selectedJobId}`,
            method: 'GET',
            headers: { Authorization: idToken }
        };

        Axios(getMLJob).then(({ data: { jobs } }) => {
            const job = jobs.find((e) => e.jobId === selectedJobId);
            if (!job) throw new Error(`Job ${selectedJobId} not found!`);

            if (job.status === 'error') {
                this.loadingJob = null;
                return this.setState({ loading: null, data: null, error: job.error });
            }
            if (job.status !== 'done') {
                // created or running — say how long, and come back.
                this.loadingJob = null;
                this.setState({
                    loading: <CircularProgress />, data: null,
                    error: `Job's current status is '${job.status}', run time `
                        + `${Moment().diff(Moment(job.createdAt), 'minutes')} minutes...`
                });
                this.clearTimer();
                this.loadingTimer = setTimeout(() => {
                    if (this.state.selectedJobId === selectedJobId) this.loadJob();
                }, JOB_POLL_INTERVAL);
                return;
            }
            if (!job.result) {
                this.loadingJob = null;
                return this.setState({ loading: null, data: null, error: 'Job is done, but has no result.' });
            }

            this.setState({ loading: <CircularProgress />, error: null });
            return loadJobResult(this.props.appContext, job).then((loaded) => {
                // The user may have moved on while S3 was answering.
                if (this.state.selectedJobId !== selectedJobId) {
                    throw new Error(`Loading of job ${selectedJobId} cancelled!`);
                }
                if (typeof loaded.result === 'string' && !loaded.result.trim().startsWith('{')) {
                    // An older run of a chart algorithm returned a rendered PNG rather than data. Say so
                    // plainly — it is a stale result, not a broken view, and re-running fixes it.
                    throw new Error('This run returned an image rather than data. Re-run the job to get a '
                        + 'result this view can draw.');
                }
                this.setState({
                    loading: null, job: loaded, data: JSON.parseMore(loaded.result), error: null
                });
                this.loadingJob = null;
            });
        }).catch((error) => {
            this.setState({ loading: null, data: null, error: getErrorMessage(error) });
            this.loadingJob = null;
        });
    }

    render() {
        const { jobs, selectedJobId, data, progress, loading, error } = this.state;
        const renderer = RENDERERS[this.resultFormat];

        let body = null;
        if (loading) {
            body = <div style={{ padding: 30, textAlign: 'center' }}>{loading}</div>;
        } else if (!renderer) {
            body = <Typography color="error">No renderer for '{this.resultFormat}'.</Typography>;
        } else if (data) {
            body = renderer.render(data);
        } else if (!selectedJobId) {
            body = <Typography variant="body2" style={{ padding: 20, opacity: 0.7 }}>
                {jobs.length
                    ? 'Select a job result above.'
                    : `No finished jobs produce ${this.resultFormat} data for this plan yet. An algorithm `
                      + `declares what it produces with "resultFormat" in its manifest.`}
            </Typography>;
        }

        return <div style={{ padding: 10 }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <FormControl style={{ margin: 5, width: 420 }}>
                    <InputLabel>{`Select ${renderer ? renderer.title : 'Job'} Result`}</InputLabel>
                    <Select value={selectedJobId} onChange={(e) => {
                        this.clearTimer();
                        this.setState({ selectedJobId: e.target.value, data: null, error: null },
                            this.loadJob.bind(this));
                    }}>
                        {jobs.map((j) => <MenuItem key={j.jobId} value={j.jobId}>
                            {`${Moment(j.createdAt).format('YYYY-MM-DD HH:mm')}: ${j.name || j.algorithm}`}
                        </MenuItem>)}
                    </Select>
                </FormControl>
                {progress}
                <span style={{ color: 'red', marginLeft: 10 }}>{error}</span>
            </div>
            {body}
        </div>;
    }
}
